import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import { getDeviceAccount, getDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { Platform } from "@-/com.npmjs.react-native";
import { useDerivedValue, useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { type PropsWithChildren, useCallback, useMemo, useRef, useState } from "react";
import { AuthSheetContent } from "#auth-sheet-content";
import { AuthSheetContext, type AuthSheetApi, type AuthSheetRequest } from "#auth-sheet-context";

/**
 * The app's one auth sheet. The welcome deck's "Get Started" and "Log in" and every entry point
 * for a device that is not signed in (the account drawer's unverified or expired account, the
 * Settings profile button, the first-level prompt) open this same sheet over the current screen
 * instead of navigating to an auth route; they only choose which side of its Log In / Sign Up
 * toggle it opens on.
 *
 * Mount once, inside the router and above every consumer. The sheet is portalled by the root
 * `BottomSheetModalProvider`, so its position in the tree does not affect layout.
 */
export function AuthSheetProvider({ children }: PropsWithChildren) {
  const sheetRef = useRef<BottomSheetHandle>(null);
  const [isInlineWelcome, setIsInlineWelcome] = useState(false);
  const [request, setRequest] = useState<AuthSheetRequest>({
    email: "",
    mode: "signIn",
    openCount: 0,
    signUpKind: "getStarted",
  });
  // The sheet's position, -1 (closed) to 0 (open); the sheet only writes it while mounted.
  const sheetIndex = useSharedValue(-1);
  const presentation = useDerivedValue(() =>
    isInlineWelcome ? 1 : Math.min(Math.max(sheetIndex.get() + 1, 0), 1),
  );
  const onDismiss = useCallback(() => sheetIndex.set(-1), [sheetIndex]);

  const openAccount = useCallback<AuthSheetApi["openAccount"]>((options) => {
    const source = options?.source ?? "unknown";
    // The same split as `useLocalAccount`: an anonymous local account activates its address
    // through sign-up; a device that was signed in (its session expired) logs back in with it.
    const accountEmail = getDeviceSetting("accountEmail");
    const mode =
      options?.mode ?? (accountEmail != null && getDeviceAccount() == null ? "signUp" : "signIn");
    setRequest((previous) => ({
      email: accountEmail ?? "",
      mode,
      openCount: previous.openCount + 1,
      // Only an explicit or activating sign-up needs a password; switching to Sign Up from a
      // Log In request offers the same guest path as "Get Started".
      signUpKind: mode === "signUp" ? "account" : "getStarted",
    }));
    const inline = Platform.OS === "web" && source === "welcome";
    setIsInlineWelcome(inline);
    if (!inline) {
      sheetRef.current?.open();
    }
    captureAnalyticsEvent("auth_sheet_opened", {
      mode: mode === "signUp" ? "sign_up" : "sign_in",
      source,
    });
  }, []);
  const openGetStarted = useCallback<AuthSheetApi["openGetStarted"]>((options) => {
    setRequest((previous) => ({
      email: "",
      mode: "signUp",
      openCount: previous.openCount + 1,
      signUpKind: "getStarted",
    }));
    const inline = Platform.OS === "web" && options?.source === "welcome";
    setIsInlineWelcome(inline);
    if (!inline) {
      sheetRef.current?.open();
    }
    captureAnalyticsEvent("auth_sheet_opened", {
      mode: "sign_up",
      source: options?.source ?? "unknown",
    });
  }, []);
  const close = useCallback(() => {
    setIsInlineWelcome(false);
    sheetRef.current?.close();
  }, []);

  const api = useMemo<AuthSheetApi>(
    () => ({
      close,
      openAccount,
      openGetStarted,
      presentation,
      welcomeRequest: isInlineWelcome ? request : null,
    }),
    [close, openAccount, openGetStarted, presentation, isInlineWelcome, request],
  );

  return (
    <AuthSheetContext value={api}>
      {children}
      <BottomSheet animatedIndex={sheetIndex} onDismiss={onDismiss} ref={sheetRef} scrollable>
        <AuthSheetContent
          close={close}
          email={request.email}
          initialMode={request.mode}
          key={request.openCount}
          signUpKind={request.signUpKind}
        />
      </BottomSheet>
    </AuthSheetContext>
  );
}
